import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "input-elements"
    }}>{`Input elements`}</h2>
    <p>{`As any input element is highly likely to contain sensitive text such as email or password, `}<strong parentName="p">{`we mask these by default`}</strong>{`. You can explicitly set this to false to disable the masking. You can then specify inputs types you would like to be masked.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
            password: true, // Highly recommended as a minimum!!
            // color: false,
            // date: false,
            // 'datetime-local': false,
            // email: false,
            // month: false,
            // number: false,
            // range: false,
            // search: false,
            // tel: false,
            // text: false,
            // time: false,
            // url: false,
            // week: false,
            // textarea: false,
            // select: false,
    }
})
`}</code></pre>
    <h3 {...{
      "id": "mask-or-un-mask-specific-inputs"
    }}>{`Mask or un-mask specific inputs`}</h3>
    <p>{`You can control the masking more granularly by using `}<inlineCode parentName="p">{`maskInputFn`}</inlineCode>{` to customize how the masking behaves. For example, you may want to only redact text that looks like an email address, or comes from inputs that aren't search boxes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        // \`maskInputFn\` only applies to selected elements, so make sure to set this to true if you want this to work globally
        maskAllInputs: true,
        maskInputFn: (text, element) => {
            if (element?.attributes['type']?.value === 'search') {
                // If this is a search input, don't mask it
                return text
            }
            // Otherwise, mask it with asterisks
            return '*'.repeat(text.length)
        },
    }
})
`}</code></pre>
    <h2 {...{
      "id": "text-elements"
    }}>{`Text elements`}</h2>
    <p>{`General text is not masked by default, but we provide multiple options for masking text:`}</p>
    <h3 {...{
      "id": "mask-all-text"
    }}>{`Mask all text`}</h3>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`IMPORTANT`}</strong>{`: The `}<inlineCode parentName="p">{`text`}</inlineCode>{` related config options only apply to non-input text. Inputs are masked differently and have separate methods (as detailed `}<a parentName="p" {...{
          "href": "#input-elements"
        }}>{`above`}</a>{`).`}</p>
    </blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        maskTextSelector: "*" // Masks all text elements (not including inputs)
    }
})
`}</code></pre>
    <h3 {...{
      "id": "mask-or-un-mask-specific-text"
    }}>{`Mask or un-mask specific text`}</h3>
    <p>{`You can use a CSS selector to mask specific elements. For example, you may want to mask all elements with the class `}<inlineCode parentName="p">{`email`}</inlineCode>{` or the ID `}<inlineCode parentName="p">{`sensitive`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        maskTextSelector: ".email, #sensitive" // masks all elements with the class "email" or the ID "sensitive". This does not apply to input elements.

    }
})
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`IMPORTANT`}</strong>{`: The masking of an element applies to its children meaning `}<inlineCode parentName="p">{`:not`}</inlineCode>{` selectors do not work. The ability to selectively mask elements is detailed `}<a parentName="p" {...{
          "href": "#selective-privacy---only-reveal-things-that-are-marked-as-safe"
        }}>{`below`}</a></p>
    </blockquote>
    <p>{`You can further control the text that gets masked. For example, by only masking text that looks like an email`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`posthog.init('<ph_project_api_key>', {
    session_recording: {
        // \`maskTextFn\` only applies to selected elements, so make sure to set to "*" if you want this to work globally.
        // This does not apply to input elements.
        maskTextSelector: "*", 
        maskTextFn: (text) => {
            // this fn can receive whitespace only text, you might not want to mask that
            if (text.trim().length === 0) { return text }

            // A simple email regex - you may want to use something more advanced
            const emailRegex = /(\\S+)@(\\S+\\.\\S+)/g

            return text.replace(emailRegex, (match, g1, g2) => {
                // Replace each email with asterisks - ben@posthog.com becomes ***@***********
                return '*'.repeat(g1.length) + '@' + '*'.repeat(g2.length)
            })
        },
    }
})
`}</code></pre>
    <h2 {...{
      "id": "other-elements"
    }}>{`Other Elements`}</h2>
    <p>{`If your application displays sensitive user information outside of input or text fields, or if there are areas of your application that you simply don't want to capture, you need to update your codebase to prevent PostHog from capturing this information during session recordings.`}</p>
    <p>{`To do so, you should add the CSS class name `}<inlineCode parentName="p">{`ph-no-capture`}</inlineCode>{` to elements which should not be recorded. This will lead to the element being replaced with a block of the same size when you play back the recordings. Make sure everyone who watches recordings in your team is aware of this, so that they don't think your product is broken when something doesn't show up!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<div class="ph-no-capture">I won't be captured at all!</div>
`}</code></pre>
    <p>{`Note that adding `}<inlineCode parentName="p">{`ph-no-capture`}</inlineCode>{` will also prevent any `}<a parentName="p" {...{
        "href": "/docs/data/autocapture"
      }}>{`autocapture`}</a>{` events from being captured from that element.`}</p>
    <h2 {...{
      "id": "common-example-configs"
    }}>{`Common example configs`}</h2>
    <h3 {...{
      "id": "maximum-privacy---mask-everything"
    }}>{`Maximum privacy - mask everything`}</h3>
    <pre><code parentName="pre" {...{}}>{`{
    maskAllInputs: true,
    maskTextSelector: "*"
}
`}</code></pre>
    <h3 {...{
      "id": "limited-privacy---try-to-mask-all-email--password-related-things"
    }}>{`Limited privacy - try to mask all email / password related things`}</h3>
    <p>{`You can mask content that looks like it contains an email or password.`}</p>
    <p>{`For passwords, it is important to note that "click to show password" buttons typically turn the input type to `}<inlineCode parentName="p">{`text`}</inlineCode>{`. This would then reveal the password. Thus instead of checking for `}<inlineCode parentName="p">{`type='password'`}</inlineCode>{`, you need to check a different field, like `}<inlineCode parentName="p">{`id`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`{
    maskAllInputs: true,
    maskInputFn: (text, element) => {
        const maskTypes = ['email', 'password']

        if (
            maskTypes.indexOf(element?.attributes['type']?.value) !== -1 ||
            maskTypes.indexOf(element?.attributes['id']?.value) !== -1
        ) {
            return '*'.repeat(text.length)
        }
        return text
    },
    maskTextSelector: '*',
    maskTextFn(text) {
        // A simple email regex - you may want to use something more advanced
        const emailRegex = /(\\S+)@(\\S+\\.\\S+)/g

        return text.trim().replace(emailRegex, (match, g1, g2) => {
            // Replace each email with asterisks - ben@posthog.com becomes ***@***********
            return '*'.repeat(g1.length) + '@' + '*'.repeat(g2.length)
        })
    }
}
`}</code></pre>
    <h3 {...{
      "id": "selective-privacy---only-reveal-things-that-are-marked-as-safe"
    }}>{`Selective privacy - only reveal things that are marked as safe`}</h3>
    <p>{`Instead of selectively masking, we can selectively `}<em parentName="p">{`unmask`}</em>{` fields that you are sure are safe.
This assumes you are adding a data attribute to every "safe" element like `}<inlineCode parentName="p">{`<p data-record="true">I will be visible!</p>`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`{
    maskAllInputs: true,
    maskTextSelector: "*",
    maskTextFn: (text, element) => {
        if (element?.dataset['record'] === 'true') {
            return text
        }
        return '*'.repeat(text.trim().length)
    },
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      